import {geocodedAddressResultToString} from '@/utils/geocodedAddressResult';
import {
  type GeocodedAddress,
  geocodedAddressResultsToGeocodedAddresses,
  type SavedAddress,
} from '@/utils/googleAutocomplete';
import {safeSessionStorage} from '@/utils/storage';
import {type WhereSelection} from './EventBarV2Provider';

export const RECENTLY_USED_ADDRESS_LIMIT = 3;
const STORAGE_KEY = 'recentlyUsedAddresses';

export type RecentAddress = {
  mainText: string;
  secondaryText: string;
  displayValue: string;
  geocoded: GeocodedAddress;
  usedAt: string;
};

const geocodedFromSelection = (selection: WhereSelection): GeocodedAddress | null => {
  if (selection.kind === 'geocoded') return selection.geocoded;
  if (selection.kind === 'geolocation')
    return geocodedAddressResultsToGeocodedAddresses([selection.geocodedAddress])[0] ?? null;
  return null;
};

// Split a geocoded address into the two display lines the Where rows render.
const displayLinesFromGeocoded = ({
  street,
  city,
  state,
  zip,
}: GeocodedAddress): {mainText: string; secondaryText: string} => {
  const stateZip = [state, zip].filter(Boolean).join(' ').trim();
  if (street) {
    return {mainText: street, secondaryText: [city, stateZip].filter(Boolean).join(', ').trim()};
  }
  // No street (e.g. a coarse geolocation result): promote the city to the primary line.
  return {mainText: city, secondaryText: stateZip};
};

export const whereSelectionToRecentAddress = (selection: WhereSelection): RecentAddress | null => {
  const geocoded = geocodedFromSelection(selection);
  if (!geocoded) return null;
  const displayValue = geocodedAddressResultToString(geocoded);
  if (!displayValue) return null;
  return {
    ...displayLinesFromGeocoded(geocoded),
    displayValue,
    geocoded,
    usedAt: new Date().toISOString(),
  };
};

// Adapt a stored recent address into the `Address` shape `WhereAddressOption`
export const recentAddressToDisplayAddress = (recent: RecentAddress): SavedAddress => ({
  mainText: recent.mainText,
  secondaryText: recent.secondaryText,
  name: recent.displayValue,
  placeId: recent.displayValue,
  isAutocompleteResult: false,
  street: recent.geocoded.street,
  city: recent.geocoded.city,
  state: recent.geocoded.state,
});

const isRecentAddress = (value: unknown): value is RecentAddress => {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.displayValue === 'string' &&
    typeof entry.mainText === 'string' &&
    typeof entry.secondaryText === 'string' &&
    typeof entry.geocoded === 'object' &&
    entry.geocoded !== null
  );
};

const parseStored = (raw: string | null): RecentAddress[] => {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isRecentAddress) : [];
  } catch {
    return [];
  }
};

const dedupeKey = (address: Pick<RecentAddress, 'displayValue'>): string =>
  address.displayValue.toLowerCase().trim();

// Record an address the user just searched with. No-op for selections we can't (or don't) store.
export const recordRecentlyUsedAddress = (selection: WhereSelection | null): void => {
  if (!selection) return;
  const entry = whereSelectionToRecentAddress(selection);
  if (!entry) return;
  const key = dedupeKey(entry);
  const stored = parseStored(safeSessionStorage.getItem(STORAGE_KEY));
  const updated = [entry, ...stored.filter(address => dedupeKey(address) !== key)].slice(
    0,
    RECENTLY_USED_ADDRESS_LIMIT,
  );
  safeSessionStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
};

export const getRecentlyUsedAddresses = (): RecentAddress[] =>
  parseStored(safeSessionStorage.getItem(STORAGE_KEY));
